import { Button } from "primereact/button";
import "./transaction-summary.scss";
import { useTransactionStepContext } from "../transaction-step.context";
import { DataTable } from "primereact/datatable";
import ParentInfo, { ParentInfoMember } from "@core/ui/parent-info/parent-info";
import { Divider } from "primereact/divider";
import { useNewTransactionContext } from "../new-transaction.context";
import { Column } from "primereact/column";
import { useCallback } from "react";
import { keyBy } from "lodash-es";
import { Accordion, AccordionTab } from "primereact/accordion";
import ServiceInfo from "@core/ui/service-info/service-info";
import { currencyFormat } from "@shared/utility/formats/currency-format";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { format } from "date-fns";
import TextBlock from "@shared/ui/text-block/text-block";
import { MemberFormMap } from "@core/model/member-form-map";
import { MemberFormSchema } from "@factory/form/form.rule";

export interface TransactionSummaryProps {}

export function TransactionSummary({}: TransactionSummaryProps) {
  const {
    parentInfo,
    memberInfo,
    isCreating,
    handlePrint,
    createTransactionFromServices,
    pendingParentForm,
  } = useNewTransactionContext();
  const {
    isParentAndChild,
    parentFormData,
    memberFormData,
    stepperRef,
    serviceFormData,
    childrenList,
    childFirstList,
    calculateTransaction,
    getAddonSummary,
    isCreateNewChild,
    childFormData,
    getActiveMemberEndDateTime,
  } = useTransactionStepContext();
  const { formatDate, getDateTimeLocal } = useDateTimeContext();

  const handlePreview = async (isPaid: boolean) => {
    createTransactionFromServices(isPaid);
  };

  const getServiceData = useCallback(() => {
    let childRecord: Record<string, { label: string, value: string }> = {};

    if (isCreateNewChild) {
      childFormData.forEach((child, index) => {
        const name = `${child.firstName || ""} ${child.lastName || ""}`.trim() || `Child #${index + 1}`;
        childRecord[`new-${index}`] = { label: name, value: `new-${index}` };
      });
    } else {
      const combinedChildren = [...childrenList, ...childFirstList];
      childRecord = keyBy(combinedChildren, "value") as any;
    }

    const serviceFullData = serviceFormData.map((item) => {
      let date = item.date;
      let time = item.time;
      const manilaNow = getDateTimeLocal(new Date(), "Asia/Manila");
      let createdAt = manilaNow.toISOString();

      if (!date && !time && item.member) {
        const activeEnd = getActiveMemberEndDateTime(item.member);
        if (activeEnd) {
          const manilaActiveEnd = getDateTimeLocal(activeEnd, "Asia/Manila");
          date = format(manilaActiveEnd, "yyyy-MM-dd");
          time = format(manilaActiveEnd, "HH:mm");
          createdAt = manilaActiveEnd.toISOString();
        } else {
          date = format(manilaNow, "yyyy-MM-dd");
          time = format(manilaNow, "HH:mm");
        }
      } else if (!date && !time) {
        date = format(manilaNow, "yyyy-MM-dd");
        time = format(manilaNow, "HH:mm");
      }

      return {
        ...item,
        childInfo: item.member ? childRecord[item.member] : null,
        serviceData: {
          ...item.serviceData,
          service_name: item.serviceData.name,
          service_description: item.serviceData.description,
          date,
          time,
          created_at: createdAt,
        },
      };
    });
    return serviceFullData;
  }, [
    serviceFormData,
    childrenList,
    childFirstList,
    isCreateNewChild,
    childFormData,
    getActiveMemberEndDateTime,
    getDateTimeLocal,
  ]);

  const getDayType = useCallback((dateStr?: string) => {
    try {
      if (!dateStr) {
        const today = new Date();
        const day = today.getDay();
        return day === 0 || day === 6 ? "Weekend" : "Weekdays";
      }
      const parts = dateStr.split("-");
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // 0-indexed
        const day = parseInt(parts[2], 10);
        const date = new Date(year, month, day);
        const dayOfWeek = date.getDay();
        return dayOfWeek === 0 || dayOfWeek === 6 ? "Weekend" : "Weekdays";
      }
      return "Weekdays";
    } catch {
      return "Weekdays";
    }
  }, []);

  // RENDER
  const summaryElement = (
    <div className="w-full flex flex-col gap-6 text-gray-800">
      {/* Top Header: SUMMARY on left, TOTAL centered */}
      <div className="grid grid-cols-1 md:grid-cols-3 items-start w-full gap-4">
        <div></div>

        <div className="flex flex-col items-center justify-center">
          <span className="text-sm font-semibold uppercase tracking-wider text-gray-400">Total:</span>
          <span className="text-2xl font-bold text-gray-900 mt-1">
            {currencyFormat(calculateTransaction)}
          </span>
        </div>
        
        <div className="hidden md:block"></div>
      </div>



      {/* Children / Services List Table */}
      <div className="flex flex-col w-full">
        <div className="grid grid-cols-3 pb-2 text-sm font-bold text-gray-600">
          <span className="uppercase tracking-wider underline underline-offset-4">
            {isParentAndChild ? "Children" : "Member"}
          </span>
          <span className="uppercase tracking-wider text-center underline underline-offset-4">Duration/Type</span>
          <span className="uppercase tracking-wider text-right underline underline-offset-4">Amount</span>
        </div>
        <div className="flex flex-col mt-2">
          {getServiceData().map((item, idx) => {
            const displayName = item.childInfo?.label || (memberInfo ? `${memberInfo.first_name} ${memberInfo.last_name}` : `${memberFormData?.firstName || ""} ${memberFormData?.lastName || ""}`.trim()) || "Member";
            return (
              <div key={item.childInfo?.value || idx} className="grid grid-cols-3 py-3 items-center text-sm">
                <span className="font-semibold text-gray-800">{displayName}</span>
                <span className="text-gray-600 text-center flex items-center justify-center">
                  <span className="font-medium">{item.serviceData?.service_name || "-"}</span>
                  <span className="text-xs text-gray-400 ml-1">{getDayType(item.date)}</span>
                </span>
                <span className="font-mono text-gray-800 text-right">
                  {currencyFormat(item.amount || 0)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Addons List Table */}
      {getAddonSummary().length > 0 && (
        <>
          <div className="flex flex-col w-full mt-6">
            <div className="grid grid-cols-4 pb-2 text-sm font-bold text-gray-600">
              <span className="uppercase tracking-wider underline underline-offset-4">Addons</span>
              <span className="uppercase tracking-wider text-center underline underline-offset-4">Quantity</span>
              <span className="uppercase tracking-wider text-center underline underline-offset-4">Amount</span>
              <span className="uppercase tracking-wider text-right underline underline-offset-4">Total</span>
            </div>
            <div className="flex flex-col mt-2">
              {getAddonSummary().map((addon, idx) => (
                <div key={idx} className="grid grid-cols-4 py-3 items-center text-sm">
                  <span className="font-semibold text-gray-800">{addon.name}</span>
                  <span className="text-center text-gray-600 font-mono">{addon.quantity}</span>
                  <span className="text-center text-gray-600 font-mono">{currencyFormat(addon.amount)}</span>
                  <span className="font-mono text-gray-800 text-right">
                    {currencyFormat(addon.total)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div id="TransactionSummary" className="transaction-summary w-full">
      <section className="flex flex-col w-full mb-6">
        <div className="flex flex-col items-center border border-gray-200 rounded-xl bg-white p-8 shadow-sm">
          {summaryElement}
        </div>
      </section>
      <section className="flex pt-4 justify-between">
        <Button
          label="Back"
          severity="success"
          outlined
          icon="pi pi-arrow-left"
          onClick={() => (stepperRef as any).current.prevCallback()}
        />

        <section className="flex gap-2">
          <Button
            label="Print"
            disabled={isCreating}
            onClick={() => handlePrint()}
            outlined
            severity="secondary"
          ></Button>
          <Button
            label="Book"
            disabled={isCreating}
            onClick={() => handlePreview(false)}
          ></Button>
          <Button
            label="Book and Pay"
            disabled={isCreating}
            onClick={() => handlePreview(true)}
          ></Button>
        </section>
      </section>
    </div>
  );
}

export default TransactionSummary;
